import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Browser storage is optional (preferences, drafts, caches): a full quota must never crash the page.
// On quota errors, drop the admin gallery cache (largest, rebuildable) and retry once, else skip the write.
if (typeof Storage !== "undefined" && !Storage.prototype.__apolloSafeSet) {
  const nativeSetItem = Storage.prototype.setItem;
  Storage.prototype.setItem = function safeSetItem(key, value) {
    try {
      return nativeSetItem.call(this, key, value);
    } catch (e) {
      try {
        Object.keys(this).filter((k) => k.startsWith("apollo-fabric-gallery:")).forEach((k) => this.removeItem(k));
        return nativeSetItem.call(this, key, value);
      } catch {
        console.warn(`Storage full — skipped saving "${key}"`);
      }
    }
  };
  Storage.prototype.__apolloSafeSet = true;
}

// Suppress ResizeObserver loop error - this is a known browser issue that doesn't affect functionality
// https://github.com/WICG/resize-observer/issues/38
// This error is harmless and occurs when ResizeObserver can't deliver all observations in a single animation frame

// Method 1: Override window.onerror
const originalOnError = window.onerror;
window.onerror = function(message, source, lineno, colno, error) {
  if (message && typeof message === 'string' && message.includes('ResizeObserver')) {
    return true;
  }
  if (originalOnError) {
    return originalOnError(message, source, lineno, colno, error);
  }
  return false;
};

// Method 2: Add error event listener
window.addEventListener('error', (e) => {
  if (e.message && e.message.includes('ResizeObserver')) {
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
  }
}, true);

// Method 3: Override ResizeObserver to catch errors internally
const OriginalResizeObserver = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
  constructor(callback) {
    super((entries, observer) => {
      // Use requestAnimationFrame to batch observations and avoid loop errors
      window.requestAnimationFrame(() => {
        try {
          callback(entries, observer);
        } catch (e) {
          // Silently ignore ResizeObserver errors
        }
      });
    });
  }
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
